import type { Capability } from "@micropod/contracts";
import type { Decision } from "@micropod/domain";
import { ServiceWindowMeter } from "@micropod/ui";
import { useState } from "react";

/**
 * The composer.
 *
 * Every state here is DERIVED from the server's capability and decision — none of it is
 * WhatsApp logic reimplemented in React. That is what makes a review reply box the same
 * component with a different descriptor.
 *
 * A blocked state is never a disabled box with no explanation: it says which rule stopped
 * the send and what would unblock it. Compliance stops feeling like an obstruction at
 * exactly the moment it explains itself. See docs/03-design-system.md §13.4.
 */
export function Composer({
  capability,
  freeform,
  template,
  sending,
  onSend,
}: {
  capability: Capability;
  freeform: Decision;
  template: Decision;
  sending: boolean;
  onSend: (text: string) => void;
}) {
  const [text, setText] = useState("");
  const blocked = !freeform.allow ? freeform : null;
  const templatePossible = template.allow;

  if (blocked) {
    return (
      <div className="composer composer-blocked">
        <div className="blocked-head">
          <span className={`blocked-dot ${blocked.code}`} aria-hidden="true" />
          <p className="blocked-reason">{blocked.reason}</p>
        </div>
        {blocked.unblockedBy && <p className="blocked-fix">{blocked.unblockedBy}</p>}
        <div className="composer-foot">
          <ServiceWindowMeter expiresAt={capability.freeform.windowExpiresAt} />
          {templatePossible && (
            <button type="button" className="btn" disabled>
              Choose a template
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <form
      className="composer"
      onSubmit={(e) => {
        e.preventDefault();
        const value = text.trim();
        if (!value || sending) return;
        setText("");
        onSend(value);
      }}
    >
      <div className="composer-row">
        {/* field-sizing grows this with its content — no resize script listening. */}
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              e.currentTarget.form?.requestSubmit();
            }
          }}
          placeholder="Reply…"
          rows={1}
          aria-label="Message"
        />
        <button type="submit" className="btn primary" disabled={!text.trim() || sending}>
          {sending ? "Sending…" : "Send"}
        </button>
      </div>
      <div className="composer-foot">
        <ServiceWindowMeter expiresAt={capability.freeform.windowExpiresAt} />
        <span className="hint">Enter to send · Shift+Enter for a new line</span>
      </div>
    </form>
  );
}
